'use client'

import { useState, useEffect, useRef } from 'react'
import AvaIconOrb from './AvaIconOrb'

export default function HeroChaos({ speed = 1 }) {
  const [phase, setPhase] = useState(0)
  const [demoIdx, setDemoIdx] = useState(0)
  const reduceRef = useRef(false)

  useEffect(() => {
    reduceRef.current = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  }, [])

  useEffect(() => {
    if (reduceRef.current) { setPhase(2); return }
    const durations = [2800, 1500, 3000, 4200]
    const t = setTimeout(() => {
      setPhase(p => (p + 1) % 4)
    }, durations[phase] / speed)
    return () => clearTimeout(t)
  }, [phase, speed])

  useEffect(() => {
    if (phase !== 3) { setDemoIdx(0); return }
    const t = setInterval(() => setDemoIdx(i => i + 1), 1200 / speed)
    return () => clearInterval(t)
  }, [phase, speed])

  const apps = [
    { name: 'Tasks',    kind: 'tasks',    color: '#FF5A5F', angle: 0 },
    { name: 'Mail',     kind: 'mail',     color: '#4A90E2', angle: 36 },
    { name: 'Cal',      kind: 'cal',      color: '#E84393', angle: 72 },
    { name: 'Notes',    kind: 'notes',    color: '#FDCB6E', angle: 108 },
    { name: 'Meet',     kind: 'meet',     color: '#00B894', angle: 144 },
    { name: 'Docs',     kind: 'docs',     color: '#1E47C7', angle: 180 },
    { name: 'Chat',     kind: 'chat',     color: '#25D366', angle: 216 },
    { name: 'Rec',      kind: 'rec',      color: '#FF7675', angle: 252 },
    { name: 'Research', kind: 'research', color: '#A29BFE', angle: 288 },
    { name: 'Habits',   kind: 'habits',   color: '#55EFC4', angle: 324 },
  ]

  const isLight = phase === 0
  const collapsing = phase === 1
  const avaAlive = phase >= 2

  const demoLine = {
    user: 'Hey AVA, schedule a dentist appointment next Tuesday at 3pm',
    tasks: [
      { icon: '◷', label: 'Dentist — Tue, Nov 18 · 3:00 PM', tag: 'Calendar' },
      { icon: '✓', label: 'Bring insurance card', tag: 'Task' },
      { icon: '✉', label: 'Email Dr. Kim to confirm', tag: 'Email' },
    ],
  }

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      aspectRatio: '1 / 1',
      maxWidth: 540,
      margin: '0 auto',
    }}>
      <div style={{
        position: 'absolute',
        inset: '10%',
        borderRadius: '50%',
        background: avaAlive
          ? 'radial-gradient(circle, rgba(50,105,248,0.25), rgba(111,160,255,0.12) 40%, transparent 70%)'
          : 'radial-gradient(circle, rgba(50,105,248,0.05), transparent 60%)',
        transition: 'all 1.2s cubic-bezier(0.2, 0.8, 0.2, 1)',
        filter: 'blur(30px)',
        opacity: avaAlive ? 1 : 0.6,
      }} />

      {apps.map((app, i) => {
        const rad = 38
        const baseX = 50 + rad * Math.cos((app.angle - 90) * Math.PI / 180)
        const baseY = 50 + rad * Math.sin((app.angle - 90) * Math.PI / 180)
        const jitter = Math.sin(i * 2.7) * 2
        const x = isLight ? baseX + jitter : 50
        const y = isLight ? baseY + jitter : 50
        const scale = isLight ? 1 : collapsing ? 0.3 : 0
        const opacity = isLight ? 1 : collapsing ? 0.7 : 0
        const delay = collapsing ? i * 40 : 0

        return (
          <div key={app.name} style={{
            position: 'absolute',
            left: `${x}%`,
            top: `${y}%`,
            transform: `translate(-50%, -50%) scale(${scale}) rotate(${isLight ? Math.sin(i * 3) * 6 : 0}deg)`,
            transition: `all ${collapsing ? 900 : 700}ms cubic-bezier(0.65, 0, 0.35, 1) ${delay}ms`,
            opacity,
            zIndex: 2,
          }}>
            <div style={{
              width: 72,
              height: 72,
              borderRadius: 18,
              background: `linear-gradient(135deg, ${app.color}, ${app.color}dd)`,
              boxShadow: `0 14px 32px ${app.color}40, inset 0 1px 0 rgba(255,255,255,0.35), inset 0 -1px 0 rgba(0,0,0,0.1)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              animation: isLight ? `avaChaosFloat${i % 3} ${3 + i * 0.2}s ease-in-out infinite` : 'none',
              overflow: 'hidden',
              position: 'relative',
            }}>
              <AppGlyph kind={app.kind} />
              <div style={{
                position: 'absolute',
                top: 0, left: 0, right: 0,
                height: '45%',
                background: 'linear-gradient(180deg, rgba(255,255,255,0.25), transparent)',
                pointerEvents: 'none',
              }} />
            </div>
          </div>
        )
      })}

      {collapsing && (
        <svg style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 3,
        }}>
          {apps.map((app, i) => {
            const rad = 38
            const x1 = 50 + rad * Math.cos((app.angle - 90) * Math.PI / 180)
            const y1 = 50 + rad * Math.sin((app.angle - 90) * Math.PI / 180)
            return (
              <line key={i}
                x1={`${x1}%`} y1={`${y1}%`} x2="50%" y2="50%"
                stroke={app.color} strokeWidth="1.5" opacity="0.5"
                style={{
                  strokeDasharray: 200,
                  strokeDashoffset: 200,
                  animation: `avaDrawLine 700ms ${i * 40}ms cubic-bezier(0.65,0,0.35,1) forwards`,
                }}
              />
            )
          })}
        </svg>
      )}

      <div style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: `translate(-50%, -50%) scale(${avaAlive ? 1 : collapsing ? 0.2 : 0})`,
        transition: 'all 900ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        zIndex: 5,
      }}>
        <AvaIconOrb size={200} pulse={phase === 3} />
      </div>

      <div style={{
        position: 'absolute',
        left: '50%',
        bottom: '2%',
        transform: `translate(-50%, ${phase === 3 ? '0' : '30px'})`,
        opacity: phase === 3 ? 1 : 0,
        transition: 'all 700ms cubic-bezier(0.2, 0.8, 0.2, 1) 300ms',
        width: '86%',
        zIndex: 6,
        pointerEvents: phase === 3 ? 'auto' : 'none',
      }}>
        <DemoTranscript line={demoLine} idx={demoIdx} />
      </div>
    </div>
  )
}

function DemoTranscript({ line, idx }) {
  const [typed, setTyped] = useState('')
  useEffect(() => {
    setTyped('')
    let i = 0
    const t = setInterval(() => {
      i++
      setTyped(line.user.slice(0, i))
      if (i >= line.user.length) clearInterval(t)
    }, 28)
    return () => clearInterval(t)
  }, [line.user])

  const tasksShown = Math.min(idx, line.tasks.length)

  return (
    <div style={{
      background: 'rgba(255,255,255,0.82)',
      backdropFilter: 'blur(24px) saturate(180%)',
      WebkitBackdropFilter: 'blur(24px) saturate(180%)',
      border: '1px solid rgba(255,255,255,0.8)',
      borderRadius: 24,
      padding: '16px 18px',
      boxShadow: '0 20px 50px rgba(20,14,50,0.15), 0 2px 6px rgba(20,14,50,0.04)',
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase',
        color: '#3269F8', fontWeight: 600, marginBottom: 8,
      }}>
        <span style={{
          width: 6, height: 6, borderRadius: 3, background: '#FF3B30',
          animation: 'avaPulse 1.2s ease-in-out infinite',
        }} />
        Listening
      </div>
      <div style={{
        fontSize: 15, color: '#0E0E0C', fontWeight: 500,
        lineHeight: 1.4, minHeight: 42, marginBottom: 10,
      }}>
        {typed}
        <span style={{
          display: 'inline-block', width: 2, height: 16, background: '#3269F8',
          marginLeft: 2, verticalAlign: 'middle',
          animation: 'avaBlink 0.9s step-end infinite',
        }} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {line.tasks.map((t, i) => (
          <div key={i} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 10px',
            background: 'rgba(50,105,248,0.06)',
            borderRadius: 12,
            opacity: i < tasksShown ? 1 : 0,
            transform: `translateX(${i < tasksShown ? 0 : -10}px)`,
            transition: `all 400ms cubic-bezier(0.2,0.8,0.2,1) ${i * 80}ms`,
          }}>
            <div style={{
              width: 24, height: 24, borderRadius: 7,
              background: 'linear-gradient(135deg, #3269F8, #6FA0FF)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 13, fontWeight: 700,
            }}>{t.icon}</div>
            <div style={{ flex: 1, fontSize: 13, color: '#0E0E0C', fontWeight: 500 }}>
              {t.label}
            </div>
            <div style={{
              fontSize: 10, color: '#6B6A66', fontWeight: 600,
              padding: '3px 7px', borderRadius: 6, background: '#F0EEEA',
              letterSpacing: 0.3, textTransform: 'uppercase',
            }}>{t.tag}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function AppGlyph({ kind }) {
  const s = { stroke: '#fff', strokeWidth: 2.2, fill: 'none', strokeLinecap: 'round', strokeLinejoin: 'round' }
  const sz = 38
  switch (kind) {
    case 'tasks':
      return (
        <svg width={sz} height={sz} viewBox="0 0 32 32">
          <rect x="4" y="6" width="10" height="10" rx="2" fill="rgba(255,255,255,0.25)" />
          <path d="M6.5 10.5 L8.5 12.5 L12 8.5" {...s} />
          <rect x="4" y="18" width="10" height="10" rx="2" fill="rgba(255,255,255,0.25)" />
          <path d="M6.5 22.5 L8.5 24.5 L12 20.5" {...s} />
          <rect x="17" y="8" width="11" height="1.8" rx="0.9" fill="#fff" />
          <rect x="17" y="12" width="8" height="1.8" rx="0.9" fill="rgba(255,255,255,0.65)" />
          <rect x="17" y="20" width="11" height="1.8" rx="0.9" fill="#fff" />
          <rect x="17" y="24" width="7" height="1.8" rx="0.9" fill="rgba(255,255,255,0.65)" />
        </svg>
      )
    case 'mail':
      return (
        <svg width={sz} height={sz} viewBox="0 0 32 32">
          <rect x="4" y="8" width="24" height="17" rx="3" fill="rgba(255,255,255,0.2)" stroke="#fff" strokeWidth="2" />
          <path d="M5 10 L16 19 L27 10" stroke="#fff" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          <circle cx="24" cy="9" r="4" fill="#FF3B30" stroke="#fff" strokeWidth="1.5" />
        </svg>
      )
    case 'cal':
      return (
        <svg width={sz} height={sz} viewBox="0 0 32 32">
          <rect x="4" y="6" width="24" height="22" rx="3" fill="#fff" />
          <rect x="4" y="6" width="24" height="6" rx="3" fill="rgba(0,0,0,0.15)" />
          <rect x="8" y="3" width="2" height="6" rx="1" fill="rgba(0,0,0,0.4)" />
          <rect x="22" y="3" width="2" height="6" rx="1" fill="rgba(0,0,0,0.4)" />
          <text x="16" y="24" textAnchor="middle" fontSize="11" fontWeight="700" fill="#E84393" fontFamily="system-ui">14</text>
        </svg>
      )
    case 'notes':
      return (
        <svg width={sz} height={sz} viewBox="0 0 32 32">
          <rect x="6" y="4" width="20" height="24" rx="3" fill="rgba(255,255,255,0.2)" stroke="#fff" strokeWidth="2" />
          <rect x="9" y="9" width="14" height="2" rx="1" fill="#fff" />
          <rect x="9" y="14" width="14" height="2" rx="1" fill="#fff" />
          <rect x="9" y="19" width="10" height="2" rx="1" fill="#fff" />
          <rect x="9" y="24" width="7" height="2" rx="1" fill="#fff" />
        </svg>
      )
    case 'meet':
      return (
        <svg width={sz} height={sz} viewBox="0 0 32 32">
          <rect x="4" y="9" width="18" height="14" rx="3" fill="rgba(255,255,255,0.25)" stroke="#fff" strokeWidth="2" />
          <path d="M22 13 L28 9 L28 23 L22 19 Z" fill="#fff" />
          <circle cx="9" cy="14" r="1.5" fill="#FF3B30" />
        </svg>
      )
    case 'docs':
      return (
        <svg width={sz} height={sz} viewBox="0 0 32 32">
          <path d="M8 4 L20 4 L26 10 L26 28 L8 28 Z" fill="#fff" />
          <path d="M20 4 L20 10 L26 10" fill="rgba(0,0,0,0.15)" />
          <rect x="11" y="15" width="12" height="1.6" rx="0.8" fill="#1E47C7" />
          <rect x="11" y="19" width="12" height="1.6" rx="0.8" fill="#1E47C7" />
          <rect x="11" y="23" width="8" height="1.6" rx="0.8" fill="#1E47C7" />
        </svg>
      )
    case 'chat':
      return (
        <svg width={sz} height={sz} viewBox="0 0 32 32">
          <path d="M16 5 C9 5, 4 9, 4 15 C4 19, 7 22, 10 23 L9 28 L14 24 C15 24, 15.5 24, 16 24 C23 24, 28 20, 28 15 C28 9, 23 5, 16 5Z" fill="#fff" />
          <circle cx="11" cy="15" r="1.4" fill="#25D366" />
          <circle cx="16" cy="15" r="1.4" fill="#25D366" />
          <circle cx="21" cy="15" r="1.4" fill="#25D366" />
        </svg>
      )
    case 'rec':
      return (
        <svg width={sz} height={sz} viewBox="0 0 32 32">
          <rect x="12" y="5" width="8" height="16" rx="4" fill="#fff" />
          <path d="M7 15 C7 20, 11 24, 16 24 C21 24, 25 20, 25 15" stroke="#fff" strokeWidth="2.2" fill="none" strokeLinecap="round" />
          <rect x="15" y="24" width="2" height="4" rx="1" fill="#fff" />
          <rect x="12" y="27" width="8" height="1.8" rx="0.9" fill="#fff" />
        </svg>
      )
    case 'research':
      return (
        <svg width={sz} height={sz} viewBox="0 0 32 32">
          <circle cx="14" cy="14" r="8" fill="rgba(255,255,255,0.25)" stroke="#fff" strokeWidth="2.2" />
          <path d="M14 6 C10 10, 10 18, 14 22 M14 6 C18 10, 18 18, 14 22 M6 14 L22 14" stroke="#fff" strokeWidth="1.3" fill="none" />
          <path d="M20 20 L26 26" stroke="#fff" strokeWidth="3" strokeLinecap="round" />
        </svg>
      )
    case 'habits':
      return (
        <svg width={sz} height={sz} viewBox="0 0 32 32">
          <path d="M16 3 C17 8, 22 10, 22 16 C22 20, 19 24, 16 24 C13 24, 10 20, 10 16 C10 13, 12 11, 13 12 C13 9, 14 6, 16 3 Z" fill="#fff" />
          <path d="M16 12 C16.5 15, 19 16, 19 19 C19 21, 17.5 23, 16 23 C14.5 23, 13 21, 13 19 C13 18, 14 17, 14.5 17.5 C14.5 16, 15 14, 16 12 Z" fill="#55EFC4" />
        </svg>
      )
    default:
      return <div style={{ color: '#fff', fontSize: 24 }}>●</div>
  }
}
